/// Metrik biçimlendirme. Dashboard'daki her sayı buradan geçer — tutarlılık
/// için bileşenler kendi `toFixed`'ini yazmaz.

/// Reklam harcaması, CPC, CPM. Küçük tutarlarda kuruş önemli (CPC $0.43),
/// büyük tutarlarda değil ($12,480).
///
/// `fractionDigits` bu otomatik kararı devre dışı bırakır. Gerektiği yer:
/// grafik ekseni. Orada 0, 500, 1000, 1500 yan yana duruyor ve otomatik kural
/// yalnızca sıfıra kuruş ekleyip "$0,00 / $500 / $1.000" gibi kendi içinde
/// tutarsız bir merdiven üretiyordu.
export function formatCurrency(
  value: number,
  currency = 'USD',
  locale = 'en-US',
  fractionDigits?: number,
): string {
  const digits = fractionDigits ?? (Math.abs(value) < 100 ? 2 : 0);
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

/// Gösterim/tık gibi büyük sayılar: 1.2M, 45.3K.
export function formatCompact(value: number, locale = 'en-US'): string {
  return new Intl.NumberFormat(locale, {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

/// Tam sayı biçimi. `digits` metrik sözlüğünden geliyor (`precision`):
/// gösterim/tıklama 0 basamak, dönüşüm de öyle — platformdan ondalıklı gelse
/// bile ("3,42 dönüşüm" atıf payından doğuyor) tabloda okunacak sayı 3.
export function formatNumber(value: number, locale = 'en-US', digits?: number): string {
  return new Intl.NumberFormat(
    locale,
    digits === undefined ? undefined : { minimumFractionDigits: 0, maximumFractionDigits: digits },
  ).format(value);
}

/// CTR gibi oranlar. Girdi ondalık (0.0234), çıktı yüzde (%2.34).
export function formatPercent(value: number, locale = 'en-US', digits = 2): string {
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

/// ROAS: 3.42x
export function formatRatio(value: number, locale = 'en-US'): string {
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value)}x`;
}

/// Önceki döneme göre değişim. Bölen sıfırsa yüzde tanımsızdır — 0 veya
/// Infinity döndürmek grafikte yalan söyler, bu yüzden null.
export function percentChange(current: number, previous: number): number | null {
  if (previous === 0) return null;
  return (current - previous) / Math.abs(previous);
}

/// "14 dk önce" — ama saate BAKMADAN.
///
/// `formatRelativeTime` farkı `Date.now()` üzerinden hesaplıyor; sunucuda
/// üretilen HTML ile tarayıcıda üretilen HTML arasında saniyeler geçtiği için
/// etiket bazen farklı çıkıyor ve React hidrasyonda uyuşmazlık veriyor.
/// Demo veri zamanı zaten "kaç dakika önce" olarak taşıyor (lib/demo.ts), bu
/// yüzden dönüştürmeye saat gerekmiyor.
export function formatMinutesAgo(minutes: number, locale = 'en'): string {
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  if (minutes < 60) return rtf.format(-Math.round(minutes), 'minute');
  if (minutes < 60 * 24) return rtf.format(-Math.round(minutes / 60), 'hour');
  return rtf.format(-Math.round(minutes / (60 * 24)), 'day');
}

/// Grafik ekseni: "10 Ağu" / "Aug 10".
///
/// `timeZone: 'UTC'` şart — demo serisinin tarihleri UTC çapadan türüyor
/// (lib/demo.ts). Yerel saat dilimiyle biçimlendirilirse UTC-05'te her nokta
/// bir gün geriye kayar.
export function formatDayShort(isoDay: string, locale = 'en'): string {
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(`${isoDay}T00:00:00Z`));
}

/// "14 Eyl 2026" — fatura dönemi, ek süre bitişi, deneme sonu.
///
/// Girdi TAM ISO damga (`2026-09-14T08:17:55.529Z`), `formatDayShort`in aldığı
/// 'YYYY-MM-DD' değil: faturalama uçları anlık damga döndürüyor.
///
/// `timeZone: 'UTC'` şart ve `formatDayShort` ile aynı gerekçeden DEĞİL: orada
/// seri zaten UTC çapadan türüyordu, burada sorun HİDRASYON. Bu damgalar hem
/// sunucuda hem tarayıcıda biçimlendiriliyor; sunucunun saat dilimi ile
/// tarayıcınınki farklıysa (sunucu UTC, kullanıcı UTC+3) gün kayıyor ve React
/// uyuşmazlık bildiriyor. Sabit bir saat dilimi iki tarafta aynı dizeyi veriyor.
export function formatDate(iso: string, locale = 'en'): string {
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(iso));
}

/// "2 saat önce" — ama REFERANS ANI DIŞARIDAN.
///
/// `formatRelativeTime`in hidrasyon güvenli kardeşi. Fark tek satırda: "şu an"
/// `Date.now()`tan değil çağırandan geliyor. Sunucu bileşeni isteği işlerken
/// bir damga üretip aşağıya prop olarak indiriyor; sunucuda ve tarayıcıda aynı
/// iki girdi olduğu için aynı dize çıkıyor ve React hidrasyonda şikâyet
/// etmiyor.
///
/// Bedeli: etiket tıklamayla değil, sayfa yeniden çizildiğinde tazeleniyor.
/// "2 saat önce" yazısının saniyede bir güncellenmesinin kimseye faydası yok;
/// buna karşılık `Date.now()` okuyan bir etiket panelin her açılışında
/// hidrasyon uyarısı üretiyordu.
export function formatRelativeSince(iso: string, referenceIso: string, locale = 'en'): string {
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const seconds = Math.round((new Date(iso).getTime() - new Date(referenceIso).getTime()) / 1000);
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['second', 60],
    ['minute', 60],
    ['hour', 24],
    ['day', 7],
    ['week', 4.35],
    ['month', 12],
  ];

  let value = seconds;
  for (const [unit, limit] of units) {
    if (Math.abs(value) < limit) return rtf.format(Math.round(value), unit);
    value /= limit;
  }
  return rtf.format(Math.round(value), 'year');
}

/// "14 dk önce" — veri tazeliği etiketi (tasarım §4.1).
export function formatRelativeTime(date: Date, locale = 'en'): string {
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const seconds = Math.round((date.getTime() - Date.now()) / 1000);
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['second', 60],
    ['minute', 60],
    ['hour', 24],
    ['day', 7],
    ['week', 4.35],
    ['month', 12],
  ];

  let value = seconds;
  for (const [unit, limit] of units) {
    if (Math.abs(value) < limit) return rtf.format(Math.round(value), unit);
    value /= limit;
  }
  return rtf.format(Math.round(value), 'year');
}

/// Dosya boyutu — depolama kullanımı (`StorageUsageResponse.usedBytes`).
///
/// Taban 1024 ve birim adları KB/MB/GB: kullanıcı bu sayıyı işletim
/// sisteminin dosya yöneticisinde gördüğü sayıyla karşılaştırıyor ve
/// Windows/macOS Finder ikisi de bu tabanı gösteriyor. SI tabanı (1000, KiB)
/// teknik olarak daha doğru ama ekranda "aynı klasör orada 1,1 GB burada
/// 1,18 GB" gibi okunurdu.
///
/// Basamak sayısı büyüklüğe göre: 4,7 MB'de ondalık bilgi taşıyor, 812 KB'de
/// gürültü. Aynı karar `formatCurrency`'de de var.
export function formatBytes(bytes: number, locale = 'en-US'): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  /* Bayt ve kilobayt tam sayı; megabayttan sonra tek ondalık. */
  const digits = unit >= 2 && value < 100 ? 1 : 0;
  return `${formatNumber(value, locale, digits)} ${units[unit]}`;
}
